/**
 * EPC QR (SEPA Credit Transfer) payload builder.
 *
 * Supported by Argenta, ASN, Belfius, bunq, BNP Paribas Fortis, ING, KBC,
 * Knab, SNS and VDK — so effectively every bank this family might use.
 *
 * The payload is plain text, one field per line, encoded as a QR code.
 *
 * THE BELGIAN TRAP, stated once so it is never re-litigated:
 * a structured communication (+++090/9337/55493+++) is NOT an ISO 11649
 * creditor reference. ISO 11649 references start with RF. The structured
 * field in this payload is reserved for RF references, so the Belgian one
 * goes in the UNSTRUCTURED remittance field. Getting this backwards is the
 * single most likely reason a generated code fails to parse in a bank app.
 * `bankProfile.referenceField` exists so a bank that turns out to want it the
 * other way can be accommodated without touching this logic.
 */

import { validateIban, normaliseIban } from './iban'
import { isIso11649, formatReference, normaliseReference } from './structuredCommunication'
import { centsToPayloadAmount, type Cents } from '../money.ts'

export interface BankProfile {
  /** Where this bank expects a Belgian +++ reference. Default is correct. */
  referenceField: 'unstructured' | 'structured'
  /** Version 002 makes the BIC optional. A bank needing 001 also needs a BIC. */
  version: '001' | '002'
  requiresBic: boolean
}

export const DEFAULT_BANK_PROFILE: BankProfile = {
  referenceField: 'unstructured',
  version: '002',
  requiresBic: false,
}

export interface EpcPaymentInput {
  beneficiaryName: string
  iban: string
  amountCents: Cents | number
  /** Belgian +++ structured communication, or an RF reference, or free text. */
  remittance?: string | null
  bic?: string | null
  /** Verification gate — see below. Nothing is generated when false. */
  verified: boolean
  profile?: BankProfile
}

export type EpcResult =
  | { ok: true; payload: string; byteLength: number }
  | { ok: false; reason: EpcFailure; detail: string }

export type EpcFailure =
  | 'not_verified'
  | 'invalid_iban'
  | 'missing_name'
  | 'amount_out_of_range'
  | 'missing_bic'
  | 'payload_too_long'

const MIN_CENTS = 1
const MAX_CENTS = 99999999999 // €999,999,999.99
const MAX_PAYLOAD_BYTES = 331

/**
 * SEPA permits a restricted character set. Anything outside it is folded
 * rather than rejected — a beneficiary name with an accent should not stop a
 * payment.
 */
function sanitiseName(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9/\-?:().,'+ ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 70)
}

function sanitiseRemittance(input: string, max: number): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9/\-?:().,'+ ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max)
}

export function buildEpcPayload(input: EpcPaymentInput): EpcResult {
  const profile = input.profile ?? DEFAULT_BANK_PROFILE

  // The gate. A scannable payment code is a loaded weapon: whatever is in it
  // gets paid without anyone retyping it. So it is only ever built from data
  // a human has confirmed. Enforced here as well as in the database, because
  // this function is the last point before the money leaves.
  if (!input.verified) {
    return {
      ok: false,
      reason: 'not_verified',
      detail: 'Payment details have not been verified by a person yet.',
    }
  }

  const name = sanitiseName(input.beneficiaryName)
  if (!name) {
    return { ok: false, reason: 'missing_name', detail: 'Beneficiary name is empty.' }
  }

  const iban = validateIban(input.iban)
  if (!iban.valid) {
    return { ok: false, reason: 'invalid_iban', detail: `IBAN check failed: ${iban.reason}.` }
  }

  const amount = Number(input.amountCents)
  if (!Number.isInteger(amount) || amount < MIN_CENTS || amount > MAX_CENTS) {
    return {
      ok: false,
      reason: 'amount_out_of_range',
      detail: `Amount must be between 1 and ${MAX_CENTS} cents, got ${amount}.`,
    }
  }

  const bic = input.bic?.replace(/\s/g, '').toUpperCase() ?? ''
  if (profile.requiresBic && !bic) {
    return {
      ok: false,
      reason: 'missing_bic',
      detail: 'This bank profile requires a BIC and none is known.',
    }
  }

  // Exactly one of the two remittance fields may be populated. Both is a hard
  // violation of the standard and a common cause of a rejected code.
  let structured = ''
  let unstructured = ''

  const raw = input.remittance?.trim()
  if (raw) {
    if (isIso11649(raw)) {
      // A genuine RF reference belongs in the structured field regardless of
      // profile — that is what the field is for.
      structured = raw.replace(/\s/g, '').toUpperCase().slice(0, 25)
    } else if (normaliseReference(raw).length === 12 && !/[a-zA-Z]/.test(raw)) {
      // Belgian structured communication. Formatted with +++ so the bank app
      // recognises it as a gestructureerde mededeling rather than free text.
      const formatted = formatReference(raw)
      if (profile.referenceField === 'structured') {
        structured = formatted.slice(0, 25)
      } else {
        unstructured = formatted.slice(0, 140)
      }
    } else {
      unstructured = sanitiseRemittance(raw, 140)
    }
  }

  const lines = [
    'BCD',
    profile.version,
    '1', // character set 1 = UTF-8
    'SCT',
    bic,
    name,
    normaliseIban(iban.canonical),
    `EUR${centsToPayloadAmount(amount)}`,
    '', // purpose code — deliberately unused
    structured,
    unstructured,
    '', // beneficiary-to-originator note
  ]

  // Trailing empty lines are permitted but pointless; trim them.
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop()

  const payload = lines.join('\n')
  const byteLength = new TextEncoder().encode(payload).length

  if (byteLength > MAX_PAYLOAD_BYTES) {
    return {
      ok: false,
      reason: 'payload_too_long',
      detail: `${byteLength} bytes exceeds the ${MAX_PAYLOAD_BYTES}-byte limit.`,
    }
  }

  return { ok: true, payload, byteLength }
}
